import type {
  BookingDTO,
  CreateBookingRequest,
  OpenSlotDTO,
  RescheduleBookingRequest,
} from '@brainkey/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { clientBookingsApi } from '../api/endpoints';
import { queryKeys } from '../queryClient';

export function useOpenSlots(
  from: string,
  to: string,
  enabled = true,
): UseQueryResult<OpenSlotDTO[]> {
  return useQuery({
    queryKey: queryKeys.clientBookings.slots(from, to),
    queryFn: ({ signal }) =>
      clientBookingsApi.getOpenSlots(from, to, signal).then((res) => res.slots),
    enabled,
  });
}

export function useMyBookings(): UseQueryResult<BookingDTO[]> {
  return useQuery({
    queryKey: queryKeys.clientBookings.list,
    queryFn: ({ signal }) => clientBookingsApi.list(signal).then((res) => res.bookings),
  });
}

function invalidateBookings(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: queryKeys.clientBookings.list });
  void qc.invalidateQueries({ queryKey: ['client-bookings', 'slots'] });
}

export function useCreateBooking(): UseMutationResult<BookingDTO, unknown, CreateBookingRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBookingRequest) =>
      clientBookingsApi.create(body).then((res) => res.booking),
    onSuccess: () => invalidateBookings(qc),
  });
}

export function useRescheduleBooking(): UseMutationResult<
  BookingDTO,
  unknown,
  { id: string; startsAt: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, startsAt }: { id: string; startsAt: string }) => {
      const body: RescheduleBookingRequest = { startsAt };
      return clientBookingsApi.reschedule(id, body).then((res) => res.booking);
    },
    onSuccess: () => invalidateBookings(qc),
  });
}

export function useCancelBooking(): UseMutationResult<BookingDTO, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientBookingsApi.cancel(id).then((res) => res.booking),
    onSuccess: () => invalidateBookings(qc),
  });
}
